/**
 * Consent Management Utilities
 * Provides TypeScript integration with the consent management system
 */

interface ConsentStatus {
  status: string | { essential: boolean; analytics: boolean; marketing: boolean };
  timestamp: string;
  country: string;
}

interface ConsentAPI {
  accept: () => void;
  acceptEssential: () => void;
  acceptFromSettings: () => void;
  saveSettings: () => void;
  showSettings: () => void;
  closeSettings: () => void;
  hideBanner: () => void;
  enableAllTracking: () => void;
  enableEssentialOnly: () => void;
  enableSelectedTracking: (analytics: boolean, marketing: boolean) => void;
  getStatus: () => ConsentStatus | null;
  requiresConsent: () => boolean;
}

declare global {
  interface Window {
    LunaiConsent: ConsentAPI;
    USER_COUNTRY?: string;
  }
}

/**
 * Check if user has granted consent for analytics
 */
export function hasAnalyticsConsent(): boolean {
  if (!window.LunaiConsent) return false;

  const status = window.LunaiConsent.getStatus();
  if (!status) return false;

  // Check different consent formats
  if (status.status === 'accepted' || status.status === 'accepted-implied') {
    return true;
  }

  if (typeof status.status === 'object' && status.status.analytics) {
    return true;
  }

  return false;
}

/**
 * Check if user has granted consent for marketing
 */
export function hasMarketingConsent(): boolean {
  if (!window.LunaiConsent) return false;

  const status = window.LunaiConsent.getStatus();
  if (!status) return false;

  // Check different consent formats
  if (status.status === 'accepted' || status.status === 'accepted-implied') {
    return true;
  }

  if (typeof status.status === 'object' && status.status.marketing) {
    return true;
  }

  return false;
}

/**
 * Initialize analytics only if consent has been granted
 */
export function initAnalytics(): void {
  if (!hasAnalyticsConsent()) {
    console.log('Analytics consent not granted, skipping initialization');
    return;
  }

  // Initialize your analytics services here
  console.log('Initializing analytics with user consent');

  // Example: Google Analytics, Mixpanel, Statsig, etc.
  // if (window.gtag) {
  //   window.gtag('consent', 'update', {
  //     analytics_storage: 'granted'
  //   });
  // }
}

/**
 * Initialize marketing tracking only if consent has been granted
 */
export function initMarketing(): void {
  if (!hasMarketingConsent()) {
    console.log('Marketing consent not granted, skipping initialization');
    return;
  }

  // Initialize your marketing services here
  console.log('Initializing marketing tracking with user consent');

  // Example: Facebook Pixel, Google Ads, etc.
  // if (window.fbq) {
  //   window.fbq('consent', 'grant');
  // }
}

/**
 * Wait for consent decision and then initialize services
 */
export function waitForConsent(callback: (consent: { analytics: boolean; marketing: boolean }) => void): void {
  // Listen for consent granted event
  window.addEventListener('consent-granted', (event: CustomEvent) => {
    callback(event.detail);
  });

  // Check if consent was already granted before this listener was added
  const status = window.LunaiConsent?.getStatus();
  if (status) {
    if (status.status === 'accepted' || status.status === 'accepted-implied') {
      callback({ analytics: true, marketing: true });
    } else if (status.status === 'essential-only') {
      callback({ analytics: false, marketing: false });
    } else if (typeof status.status === 'object') {
      callback({
        analytics: status.status.analytics || false,
        marketing: status.status.marketing || false
      });
    }
  }
}

/**
 * Get user's country code from geo detection
 */
export function getUserCountry(): string | undefined {
  return window.USER_COUNTRY;
}

/**
 * Check if user is in a region that requires explicit consent
 */
export function isInStrictConsentRegion(): boolean {
  return window.LunaiConsent?.requiresConsent() || false;
}

/**
 * Programmatically show the consent settings modal
 */
export function showConsentSettings(): void {
  window.LunaiConsent?.showSettings();
}

/**
 * Revoke consent and show the banner again
 * Useful for privacy settings page
 */
export function revokeConsent(): void {
  // Clear consent from localStorage
  localStorage.removeItem('lunai_consent');

  // Reload page to show consent banner again
  window.location.reload();
}